import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import {
  LuSearch, LuPencil, LuCheck, LuWallet, LuAlertTriangle,
} from 'react-icons/lu';
import { Modal, errorMessage, fmtDate } from './trainingUtils';
import './training.css';
import './plans.css';

/**
 * Planos e Assinaturas — administrador global. Mostra o plano de cada
 * empresa e permite ajuste manual: ativar plano pago combinado por fora,
 * plano Personalizado (+50 acessos), limite de acessos sob medida, ou
 * voltar a empresa para "contrato direto" (legado, sem plano/limite).
 */

const STATUS = {
  active: { label: 'Ativo', bg: 'var(--success-soft)', color: 'var(--success)' },
  pending_payment: { label: 'Aguardando pagamento', bg: 'var(--warning-soft)', color: 'var(--warning)' },
  overdue: { label: 'Em atraso', bg: 'var(--danger-soft)', color: 'var(--danger)' },
  canceled: { label: 'Cancelado', bg: 'var(--surface-3)', color: 'var(--text-2)' },
  pending_plan: { label: 'Sem plano escolhido', bg: 'var(--surface-3)', color: 'var(--text-2)' },
};
const LEGACY = '__legacy__';

function EditModal({ row, plans, onClose, onSaved }) {
  const sub = row.subscription;
  const [form, setForm] = useState({
    plan_key: row.legacy ? LEGACY : (sub?.plan_key || 'free'),
    status: sub?.status || 'active',
    custom_max_logins: sub?.custom_max_logins ?? '',
    notes: row.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (form.plan_key === LEGACY) {
        if (!row.legacy && !window.confirm('Voltar esta empresa para contrato direto? Ela fica sem plano e sem limite de acessos, e a cobrança no Asaas é cancelada.')) { setSaving(false); return; }
        await api.delete(`/subscriptions/admin/${row.company.id}`);
      } else {
        await api.put(`/subscriptions/admin/${row.company.id}`, {
          plan_key: form.plan_key,
          status: form.status,
          custom_max_logins: form.custom_max_logins === '' ? null : Number(form.custom_max_logins),
          notes: form.notes,
        });
      }
      onSaved();
    } catch (e) {
      setError(errorMessage(e)); setSaving(false);
    }
  };

  return (
    <Modal title={`Plano — ${row.company.name}`} icon={<LuWallet color="var(--accent)" />} onClose={onClose} maxWidth={520}>
      {error && <div className="alert-custom danger">{error}</div>}
      {row.subscription?.billing_type && (
        <div className="alert-custom warning">Esta empresa tem cobrança no Asaas. Mudanças aqui ajustam o acesso no sistema; valores e cobranças devem ser conferidos no painel do Asaas.</div>
      )}
      <div>
        <label className="form-label">Plano</label>
        <select className="form-control" value={form.plan_key} onChange={(e) => set('plan_key', e.target.value)}>
          <option value={LEGACY}>Contrato direto com a DHR (sem plano e sem limite)</option>
          {plans.map((p) => (
            <option key={p.key} value={p.key}>{p.label}{p.price ? ` — R$ ${p.price}/mês` : p.price === 0 ? ' — grátis' : ' — sob proposta'}</option>
          ))}
        </select>
      </div>
      {form.plan_key !== LEGACY && (
        <>
          <div className="training-form-grid">
            <div>
              <label className="form-label">Situação</label>
              <select className="form-control" value={form.status} onChange={(e) => set('status', e.target.value)}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Limite de acessos {form.plan_key === 'enterprise' ? '*' : ''}</label>
              <input className="form-control" type="number" min="1" value={form.custom_max_logins}
                placeholder={`Padrão: ${plans.find((p) => p.key === form.plan_key)?.max_logins ?? '—'}`}
                onChange={(e) => set('custom_max_logins', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="form-label">Observações internas</label>
            <textarea className="form-control" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)}
              placeholder="Ex.: proposta aprovada em 10/2026, pagamento via transferência" />
          </div>
        </>
      )}
      <div className="training-modal-footer">
        <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}><LuCheck /> {saving ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </Modal>
  );
}

export default function SubscriptionsAdminPage() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/subscriptions/admin');
      setData(d);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar as assinaturas'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const planLabel = useMemo(() => Object.fromEntries((data?.plans || []).map((p) => [p.key, p.label])), [data]);

  if (!data) return <div className="page-card"><div className="training-hint">{error || 'Carregando…'}</div></div>;

  const rows = data.rows.filter((r) => {
    if (filter === 'legacy' && !r.legacy) return false;
    if (filter === 'paid' && !(r.subscription && ['plus', 'master', 'enterprise'].includes(r.subscription.plan_key))) return false;
    if (filter === 'attention' && !(r.subscription && ['pending_payment', 'overdue', 'pending_plan'].includes(r.subscription.status))) return false;
    const term = search.trim().toLowerCase();
    return !term || `${r.company.name} ${r.company.cnpj || ''}`.toLowerCase().includes(term);
  });

  const active = data.rows.filter((r) => r.subscription?.status === 'active');
  const mrr = active.reduce((s, r) => s + (r.subscription.price || 0), 0);
  const attention = data.rows.filter((r) => r.subscription && ['pending_payment', 'overdue'].includes(r.subscription.status)).length;

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Planos e Assinaturas</div>
          <div className="page-card-subtitle">Plano de cada empresa, situação de pagamento e acessos em uso</div>
        </div>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}
      {!data.payment_configured && (
        <div className="alert-custom warning"><LuAlertTriangle /> Pagamento online (Asaas) ainda não configurado: planos pagos ficam aguardando pagamento até você ativá-los aqui.</div>
      )}

      <div className="training-kpis">
        <div className="training-kpi"><div className="training-kpi-label">Empresas</div><div className="training-kpi-value">{data.rows.length}</div></div>
        {['free', 'plus', 'master'].map((k) => (
          <div key={k} className="training-kpi">
            <div className="training-kpi-label">{planLabel[k]}</div>
            <div className="training-kpi-value">{data.rows.filter((r) => r.subscription?.plan_key === k && r.subscription.status === 'active').length}</div>
          </div>
        ))}
        <div className="training-kpi"><div className="training-kpi-label">Receita mensal ativa</div><div className="training-kpi-value">R$ {mrr.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div></div>
        <div className="training-kpi"><div className="training-kpi-label">Pendentes / atraso</div><div className="training-kpi-value" style={{ color: attention ? 'var(--danger)' : undefined }}>{attention}</div></div>
      </div>

      <div className="training-toolbar">
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <LuSearch style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input className="form-control" style={{ paddingLeft: 32, maxWidth: 'none' }} placeholder="Buscar empresa ou CNPJ" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="training-chips" style={{ marginBottom: 0 }}>
          {[['all', 'Todas'], ['paid', 'Pagantes'], ['attention', 'Precisam de atenção'], ['legacy', 'Contrato direto']].map(([id, label]) => (
            <button key={id} type="button" className={`training-chip ${filter === id ? 'is-active' : ''}`} onClick={() => setFilter(id)}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="training-table">
          <thead><tr><th>Empresa</th><th>Plano</th><th>Situação</th><th>Acessos</th><th>Pagamento</th><th>Cliente desde</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => {
              const sub = r.subscription;
              const st = sub ? STATUS[sub.status] : null;
              return (
                <tr key={r.company.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{r.company.name}</div>
                    <div className="training-hint" style={{ marginTop: 0 }}>{r.company.cnpj || 'sem CNPJ'}{r.company.type === 'filial' ? ' · filial' : ''}</div>
                  </td>
                  <td>
                    {r.legacy ? 'Contrato direto' : r.inherited ? 'Usa o plano da matriz' : (planLabel[sub?.plan_key] || '—')}
                    {sub?.custom_max_logins ? <div className="training-hint" style={{ marginTop: 0 }}>limite personalizado</div> : null}
                    {sub && r.effective_plan_key && sub.plan_key !== r.effective_plan_key && (
                      <div className="training-hint" style={{ marginTop: 0, color: 'var(--danger)' }}>usando {planLabel[r.effective_plan_key]} até regularizar</div>
                    )}
                  </td>
                  <td>{st ? <span className="training-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span> : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.active_logins}{r.max_logins ? ` / ${r.max_logins}` : ' / ∞'}</td>
                  <td>
                    {sub?.price ? `R$ ${Number(sub.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}
                    {sub?.next_due_date && <div className="training-hint" style={{ marginTop: 0 }}>vence {fmtDate(sub.next_due_date)}</div>}
                  </td>
                  <td>{fmtDate(r.company.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="training-icon-btn" title="Ajustar plano" onClick={() => setEditing(r)}><LuPencil /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditModal row={editing} plans={data.plans} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
